import { IText } from 'fabric';
import type { PillIconMeta, PillMeta, PillTextbox } from '../types';
import { drawRoundedRect } from '../../shared/utils/draw';
import { getCachedImage } from './image-cache';

export const MIN_PILL_WIDTH = 10;

// Adds pill styles to IText objects with `pill` metadata.
type ITextProto = IText & { pill?: PillMeta };

/** Space used by the icon and its gap. */
export function iconReservedSpace(icon: PillIconMeta | undefined): number {
  return icon ? icon.width + icon.gap : 0;
}

/** Width that fits the text, padding, icon, and minWidth floor. */
export function calcPillWidth(pill: PillTextbox): number {
  const { paddingX, paddingLeft, paddingRight, minWidth, icon } = pill.pill;
  const padL = paddingLeft ?? paddingX;
  const padR = paddingRight ?? paddingX;
  const textWidth = pill.calcTextWidth?.() ?? 0;
  return Math.max(textWidth + padL + padR + iconReservedSpace(icon), minWidth, MIN_PILL_WIDTH);
}

// Draws the pill background and optional icon.
const originalRenderBackground = IText.prototype._renderBackground;

IText.prototype._renderBackground = function (ctx: CanvasRenderingContext2D) {
  const meta = (this as ITextProto).pill;

  if (!meta) {
    originalRenderBackground.call(this, ctx);
    return;
  }

  const w = this.width ?? 0;
  const h = this.height ?? 0;

  ctx.save();

  drawRoundedRect(ctx, -w / 2, -h / 2, w, h, meta.borderRadius);

  ctx.fillStyle = meta.fill;
  ctx.fill();

  if (meta.strokeWidth > 0 && meta.stroke) {
    ctx.strokeStyle = meta.stroke;
    ctx.lineWidth = meta.strokeWidth;
    ctx.stroke();
  }

  ctx.restore();

  if (meta.icon) {
    drawPillIcon(this as IText, ctx, meta);
  }
};

function drawPillIcon(pill: IText, ctx: CanvasRenderingContext2D, meta: PillMeta): void {
  const icon = meta.icon!;

  const img = getCachedImage(icon.src, () => {
    // Redraw when the icon finishes loading.
    pill.dirty = true;
    pill.canvas?.requestRenderAll();
  });

  if (!img) return;

  const w = pill.width ?? 0;
  const padL = meta.paddingLeft ?? meta.paddingX;
  const padR = meta.paddingRight ?? meta.paddingX;

  const x = icon.side === 'right' ? w / 2 - padR - icon.width : -w / 2 + padL;

  const y = -icon.height / 2;

  ctx.drawImage(img, x, y, icon.width, icon.height);
}

// Adds vertical padding to the pill height.
const originalCalcTextHeight = IText.prototype.calcTextHeight;

IText.prototype.calcTextHeight = function (): number {
  const base = originalCalcTextHeight.call(this);
  const meta = (this as ITextProto).pill;

  return meta ? base + 2 * meta.paddingY : base;
};

// Keeps the text vertically centered.
const originalGetTopOffset = IText.prototype._getTopOffset;

IText.prototype._getTopOffset = function (): number {
  const base = originalGetTopOffset.call(this);
  const meta = (this as ITextProto).pill;

  return meta ? base + meta.paddingY : base;
};

// Pill width = text + padding + icon, not just text.
const originalInitDimensions = IText.prototype.initDimensions;

IText.prototype.initDimensions = function () {
  originalInitDimensions.call(this);
  const meta = (this as ITextProto).pill;
  if (meta) this.width = calcPillWidth(this as PillTextbox);
};

// Applies padding and reserves space for the icon.
const originalGetLineLeftOffset = IText.prototype._getLineLeftOffset;

IText.prototype._getLineLeftOffset = function (lineIndex: number): number {
  const base = originalGetLineLeftOffset.call(this, lineIndex);
  const meta = (this as ITextProto).pill;

  if (!meta) return base;

  const padL = meta.paddingLeft ?? meta.paddingX;
  const padR = meta.paddingRight ?? meta.paddingX;
  const iconSpace = iconReservedSpace(meta.icon);

  const effL = meta.icon?.side === 'left' ? padL + iconSpace : padL;
  const effR = meta.icon?.side === 'right' ? padR + iconSpace : padR;

  if (this.textAlign === 'left') return base + effL;
  if (this.textAlign === 'right') return base - effR;

  return base + (effL - effR) / 2;
};
